/**
 * Quotations Module Types
 * Types for Purchase and Sales Quotations management
 */

/**
 * Quotation type (purchase from vendor / sales to customer)
 */
export type QuotationType = 'purchase' | 'sales';

/**
 * Pricing tier with quantity-based pricing
 */
export interface PricingTier {
  min_qty: number;
  max_qty?: number | null; // null means unlimited
  unit_price: number;
  currency?: string; // Optional currency override for this tier
}

/**
 * Quotation line item with part code and pricing tiers
 */
export interface QuotationItem {
  part_code: string;        // References PartCode.PartNumber (雲騰品號)
  description: string;      // Auto-filled from PartCode.VendorProduct (廠商品名)
  specification: string;    // Auto-filled from PartCode.VendorSpec (廠商規格)
  pricing_tiers: PricingTier[];
  currency: string;
  lead_time_days: number;
  notes?: string;
}

/**
 * Source file metadata for uploaded quotation documents
 */
export interface SourceFile {
  drive_file_id: string;    // Google Drive file ID
  drive_folder_id: string;  // Google Drive folder ID
  file_name: string;        // Original filename
  file_type: string;        // xlsx, pdf, csv
  web_view_link: string;    // Link to view in Drive
  uploaded_at: string;      // Upload timestamp
}

/**
 * Base quotation fields shared by both types
 */
interface BaseQuotation {
  quotation_id: string;     // PQ-xxx or SQ-xxx
  date: string;
  valid_from: string;
  valid_until: string;      // Expiry date
  items: QuotationItem[];
  currency: string;         // Default currency
  notes?: string;
  created_by: string;
  created_at: string;
  updated_at?: string;
  source_file?: SourceFile; // File attachment (Google Drive)
}

/**
 * Purchase Quotation - received FROM vendors
 */
export interface PurchaseQuotation extends BaseQuotation {
  type: 'purchase';
  vendor_code: string;      // Vendor code (供應商代碼)
  vendor_name: string;      // Vendor name (供應商名稱) - enriched
}

/**
 * Sales Quotation - sent TO customers
 */
export interface SalesQuotation extends BaseQuotation {
  type: 'sales';
  customer_code: string;    // Customer code (客戶編號)
  customer_name: string;    // Customer name (客戶名稱) - enriched
}

/**
 * Union type for any quotation
 */
export type Quotation = PurchaseQuotation | SalesQuotation;

/**
 * Type guards for quotation types
 */
export function isPurchaseQuotation(q: Quotation): q is PurchaseQuotation {
  return q.type === 'purchase';
}

export function isSalesQuotation(q: Quotation): q is SalesQuotation {
  return q.type === 'sales';
}

/**
 * Get the party code from a quotation (vendor_code or customer_code)
 */
export function getQuotationPartyCode(q: Quotation): string {
  return isPurchaseQuotation(q) ? q.vendor_code : q.customer_code;
}

/**
 * Get the party name from a quotation (vendor_name or customer_name)
 */
export function getQuotationPartyName(q: Quotation): string {
  return isPurchaseQuotation(q) ? q.vendor_name : q.customer_name;
}

/**
 * Filter options for listing purchase quotations
 */
export interface PurchaseQuotationFilter {
  [key: string]: string | number | boolean | undefined; // Index signature for buildFilterParams
  vendor_code?: string;     // Filter by vendor code
  part_code?: string;       // Filter by part code
  valid_only?: boolean;     // Only show non-expired quotations
  start_date?: string;      // Filter by date range start
  end_date?: string;        // Filter by date range end
  currency?: string;        // Filter by currency
  limit?: number;           // Pagination limit
  offset?: number;          // Pagination offset
}

/**
 * Filter options for listing sales quotations
 */
export interface SalesQuotationFilter {
  [key: string]: string | number | boolean | undefined; // Index signature for buildFilterParams
  customer_code?: string;   // Filter by customer code
  part_code?: string;       // Filter by part code
  valid_only?: boolean;     // Only show non-expired quotations
  start_date?: string;      // Filter by date range start
  end_date?: string;        // Filter by date range end
  currency?: string;        // Filter by currency
  limit?: number;           // Pagination limit
  offset?: number;          // Pagination offset
}

/**
 * Generic filter type for both quotation types
 */
export type QuotationFilter = PurchaseQuotationFilter | SalesQuotationFilter;

/**
 * List response for purchase quotations
 */
export interface PurchaseQuotationListResponse {
  quotations: PurchaseQuotation[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * List response for sales quotations
 */
export interface SalesQuotationListResponse {
  quotations: SalesQuotation[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Generic list response type
 */
export type QuotationListResponse = PurchaseQuotationListResponse | SalesQuotationListResponse;

/**
 * Request to create a new purchase quotation
 */
export interface CreatePurchaseQuotationRequest {
  date: string;
  vendor_code: string;
  valid_from: string;
  valid_until: string;
  items: QuotationItem[];
  currency: string;
  notes?: string;
}

/**
 * Request to create a new sales quotation
 */
export interface CreateSalesQuotationRequest {
  date: string;
  customer_code: string;
  valid_from: string;
  valid_until: string;
  items: QuotationItem[];
  currency: string;
  notes?: string;
}

/**
 * Union type for create requests
 */
export type CreateQuotationRequest = CreatePurchaseQuotationRequest | CreateSalesQuotationRequest;

/**
 * Request to update an existing purchase quotation
 */
export interface UpdatePurchaseQuotationRequest {
  date?: string;
  vendor_code?: string;
  valid_from?: string;
  valid_until?: string;
  items?: QuotationItem[];
  currency?: string;
  notes?: string;
}

/**
 * Request to update an existing sales quotation
 */
export interface UpdateSalesQuotationRequest {
  date?: string;
  customer_code?: string;
  valid_from?: string;
  valid_until?: string;
  items?: QuotationItem[];
  currency?: string;
  notes?: string;
}

/**
 * Union type for update requests
 */
export type UpdateQuotationRequest = UpdatePurchaseQuotationRequest | UpdateSalesQuotationRequest;

/**
 * Extracted item from AI parsing of uploaded file
 */
export interface ExtractedQuotationItem {
  vendor_part_number: string;   // Part number from supplier
  description: string;          // Description from supplier
  specification: string;        // Specification from supplier
  pricing_tiers: PricingTier[]; // Extracted pricing tiers
  currency: string;             // Detected currency
  lead_time_days: number;       // Detected lead time
  raw_text?: string;            // Original text for debugging
  confidence: number;           // AI confidence score (0-1)
}

/**
 * Result of parsing an uploaded quotation file
 */
export interface ParsedQuotation {
  vendor_name?: string;         // Detected vendor name
  quotation_date?: string;      // Detected date
  valid_until?: string;         // Detected expiry
  currency?: string;            // Detected currency
  items: ExtractedQuotationItem[];
  raw_text?: string;            // Full text for debugging
  confidence: number;           // Overall confidence
  file_type: string;            // xlsx, pdf, csv
  file_name: string;            // Original filename
}

/**
 * Mapping from vendor part number to internal part code
 */
export interface PartCodeMapping {
  vendor_part_number: string;   // Key from extracted item
  part_code: string;            // Mapped 雲騰品號
}

/**
 * Request to import a parsed purchase quotation with user mappings
 */
export interface ImportPurchaseQuotationRequest {
  type: 'purchase';
  vendor_code: string;
  date: string;
  valid_from: string;
  valid_until: string;
  currency: string;
  notes?: string;
  parsed_items: ExtractedQuotationItem[];
  mappings: PartCodeMapping[];
  source_file?: SourceFile;
}

/**
 * Request to import a parsed sales quotation with user mappings
 */
export interface ImportSalesQuotationRequest {
  type: 'sales';
  customer_code: string;
  date: string;
  valid_from: string;
  valid_until: string;
  currency: string;
  notes?: string;
  parsed_items: ExtractedQuotationItem[];
  mappings: PartCodeMapping[];
  source_file?: SourceFile;
}

/**
 * Union type for import requests
 */
export type ImportQuotationRequest = ImportPurchaseQuotationRequest | ImportSalesQuotationRequest;

/**
 * Suggestion for mapping an extracted item to a part code
 */
export interface MappingSuggestion {
  part_code: string;      // Suggested 雲騰品號
  vendor_product: string; // Part name (廠商品名)
  vendor_spec: string;    // Specification (廠商規格)
  score: number;          // Match score (0-1)
  match_reason: string;   // Why this was suggested
}

/**
 * Request to get part code suggestions
 */
export interface SuggestMappingRequest {
  description: string;
  specification?: string;
  limit?: number;
}

/**
 * Helper function to get effective price for a quantity
 */
export function getEffectivePrice(item: QuotationItem, quantity: number): number {
  // Search from highest tier down
  for (let i = item.pricing_tiers.length - 1; i >= 0; i--) {
    const tier = item.pricing_tiers[i];
    if (quantity >= tier.min_qty) {
      if (tier.max_qty === null || tier.max_qty === undefined || quantity <= tier.max_qty) {
        return tier.unit_price;
      }
    }
  }
  // Return first tier price as default
  if (item.pricing_tiers.length > 0) {
    return item.pricing_tiers[0].unit_price;
  }
  return 0;
}

/**
 * Check if a quotation is valid (not expired)
 */
export function isQuotationValid(quotation: Quotation): boolean {
  if (!quotation.valid_until) return true; // No expiry
  const expiry = new Date(quotation.valid_until);
  expiry.setHours(23, 59, 59, 999); // Valid until end of expiry date
  return new Date() <= expiry;
}

/**
 * Format pricing tier display
 */
export function formatPricingTier(tier: PricingTier, currency: string = 'TWD'): string {
  const maxQtyStr = tier.max_qty ? tier.max_qty.toString() : '∞';
  return `${tier.min_qty}-${maxQtyStr}: ${currency} ${tier.unit_price.toFixed(2)}`;
}

/**
 * Format quotation ID prefix based on type
 */
export function getQuotationIdPrefix(type: QuotationType): string {
  return type === 'purchase' ? 'PQ' : 'SQ';
}

/**
 * Default empty quotation item for form initialization
 */
export const EMPTY_QUOTATION_ITEM: QuotationItem = {
  part_code: '',
  description: '',
  specification: '',
  pricing_tiers: [{ min_qty: 1, max_qty: null, unit_price: 0 }],
  currency: 'TWD',
  lead_time_days: 14,
};

/**
 * Default empty pricing tier for form initialization
 */
export const EMPTY_PRICING_TIER: PricingTier = {
  min_qty: 1,
  max_qty: null,
  unit_price: 0,
};
